import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Product } from "@/lib/api";
import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";

interface ProductInfoProps {
  product: Product;
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState(product.colors[0]);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>("description");
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const navigate = useNavigate();

  const wishlisted = isWishlisted(product.id);

  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const discountPct = hasDiscount
    ? Math.round(((product.originalPrice! - product.price) / product.originalPrice!) * 100)
    : 0;

  const handleAddToCart = () => {
    if (!selectedSize) {
      setSizeError(true);
      return;
    }
    setSizeError(false);
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      size: selectedSize,
      color: selectedColor.name,
      colorHex: selectedColor.hex,
      quantity,
      image: product.images[0],
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const handleBuyNow = () => {
    if (!selectedSize) {
      setSizeError(true);
      return;
    }
    setSizeError(false);
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      size: selectedSize,
      color: selectedColor.name,
      colorHex: selectedColor.hex,
      quantity,
      image: product.images[0],
    });
    navigate("/checkout");
  };

  const toggleSection = (key: string) =>
    setOpenSection((prev) => (prev === key ? null : key));

  return (
    <div className="flex flex-col h-full" style={{ fontFamily: "'Jost', sans-serif" }}>
      {/* Badges */}
      <div className="flex gap-2 mb-4">
        {product.isNew && (
          <span className="text-[10px] tracking-widest uppercase bg-midnight text-moonlight px-2.5 py-1">
            New Arrival
          </span>
        )}
        {hasDiscount && (
          <span className="text-[10px] tracking-widest uppercase bg-moon-gold-dark text-midnight px-2.5 py-1">
            Save {discountPct}%
          </span>
        )}
        <span className="text-[10px] tracking-widest uppercase border border-pearl text-midnight/50 px-2.5 py-1">
          {product.fabricLabel}
        </span>
      </div>

      {/* Name */}
      <h1
        className="text-3xl md:text-4xl font-light text-midnight leading-tight mb-3"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {product.name}
      </h1>

      {/* Rating */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <i
              key={i}
              className={`text-xs ${
                i < Math.floor(product.rating)
                  ? "ri-star-fill text-moon-gold-dark"
                  : "ri-star-line text-pearl"
              }`}
            />
          ))}
        </div>
        <span className="text-xs text-midnight/50">{product.rating}</span>
        <span className="text-xs text-midnight/30">·</span>
        <span className="text-xs text-midnight/40 underline cursor-pointer">{product.reviewCount} reviews</span>
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-3 mb-5 pb-5 border-b border-pearl/40">
        <span className="text-2xl font-medium text-midnight">${product.price}</span>
        {hasDiscount && (
          <>
            <span className="text-base text-midnight/35 line-through">${product.originalPrice}</span>
            <span className="text-sm text-moon-gold-dark font-medium">Save ${product.originalPrice! - product.price}</span>
          </>
        )}
      </div>

      {/* Short Description */}
      <p className="text-sm text-midnight/60 leading-relaxed mb-6 italic">
        &ldquo;{product.shortDescription}&rdquo;
      </p>

      {/* Color Selector */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] tracking-widest uppercase text-midnight/60">Color</span>
          <span className="text-[10px] text-midnight/70">{selectedColor.name}</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {product.colors.map((color) => (
            <button
              key={color.name}
              onClick={() => setSelectedColor(color)}
              className={`w-10 h-10 rounded-full border-2 transition-all duration-200 cursor-pointer relative ${
                selectedColor.name === color.name
                  ? "border-midnight scale-110"
                  : "border-transparent hover:scale-105"
              }`}
              style={{ backgroundColor: color.hex }}
              title={color.name}
            >
              {selectedColor.name === color.name && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <i className={`ri-check-line text-sm ${isLightColor(color.hex) ? 'text-midnight' : 'text-white'}`} />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Size Selector */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] tracking-widest uppercase text-midnight/60">Select Size</span>
          <button className="text-[10px] tracking-widest uppercase text-midnight/40 underline cursor-pointer whitespace-nowrap">
            Size Guide
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {product.sizes.map((size) => (
            <button
              key={size}
              onClick={() => {
                setSelectedSize(size);
                setSizeError(false);
              }}
              className={`text-xs w-11 h-11 border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                selectedSize === size
                  ? "border-midnight bg-midnight text-moonlight"
                  : "border-pearl text-midnight/60 hover:border-midnight hover:text-midnight"
              }`}
            >
              {size}
            </button>
          ))}
        </div>
        {sizeError && (
          <p className="text-[11px] text-red-400 mt-2">Please select a size to continue</p>
        )}
      </div>

      {/* Quantity */}
      <div className="mb-6">
        <span className="text-[10px] tracking-widest uppercase text-midnight/60 block mb-3">Quantity</span>
        <div className="flex items-center border border-pearl w-fit">
          <button
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className="w-10 h-10 flex items-center justify-center text-midnight/60 hover:text-midnight hover:bg-pearl/30 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-subtract-line text-sm" />
          </button>
          <span className="w-10 text-center text-sm text-midnight font-medium">{quantity}</span>
          <button
            onClick={() => setQuantity((q) => q + 1)}
            className="w-10 h-10 flex items-center justify-center text-midnight/60 hover:text-midnight hover:bg-pearl/30 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-sm" />
          </button>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col gap-3 mb-6">
        <button
          onClick={handleAddToCart}
          className={`w-full py-4 text-sm tracking-widest uppercase font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
            addedToCart
              ? "bg-moon-gold-dark text-midnight"
              : "bg-midnight text-moonlight hover:bg-midnight-dark"
          }`}
        >
          {addedToCart ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-check-line" /> Added to Cart
            </span>
          ) : (
            "Add to Cart"
          )}
        </button>

        {/* Wishlist Button */}
        <button
          onClick={() => toggleWishlist(product.id)}
          className={`w-full py-4 text-sm tracking-widest uppercase font-medium border transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
            wishlisted
              ? "border-rose-300 bg-rose-50 text-rose-500 hover:bg-rose-100"
              : "border-pearl text-midnight/60 hover:border-midnight/40 hover:text-midnight"
          }`}
        >
          <i className={`text-base ${wishlisted ? "ri-heart-fill" : "ri-heart-line"}`} />
          {wishlisted ? "Saved to Wishlist" : "Save to Wishlist"}
        </button>

        <button
          onClick={handleBuyNow}
          className="w-full py-4 text-sm tracking-widest uppercase font-medium border border-midnight text-midnight hover:bg-midnight hover:text-moonlight transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          Buy Now
        </button>
      </div>

      {/* Service Promises */}
      <div className="grid grid-cols-3 gap-2 mb-6 pb-6 border-b border-pearl/40">
        {[
          { icon: "ri-truck-line", label: "Free Shipping", sub: "Orders $80+" },
          { icon: "ri-refresh-line", label: "Easy Returns", sub: "30-day policy" },
          { icon: "ri-secure-payment-line", label: "Secure Pay", sub: "Encrypted" },
        ].map((item) => (
          <div key={item.label} className="flex flex-col items-center text-center gap-1 py-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`${item.icon} text-lg text-midnight/50`} />
            </div>
            <span className="text-[10px] font-medium text-midnight/70">{item.label}</span>
            <span className="text-[9px] text-midnight/35">{item.sub}</span>
          </div>
        ))}
      </div>

      {/* Accordion Sections */}
      <div className="space-y-0">
        {[
          {
            key: "description",
            label: "Description",
            content: product.description,
          },
          {
            key: "material",
            label: "Material & Care",
            content: product.material,
          },
          {
            key: "fit",
            label: "Fit & Sizing",
            content: product.fit,
          },
        ].map((section) => (
          <div key={section.key} className="border-b border-pearl/40">
            <button
              onClick={() => toggleSection(section.key)}
              className="w-full flex items-center justify-between py-4 text-left cursor-pointer group whitespace-nowrap"
            >
              <span className="text-[11px] tracking-widest uppercase text-midnight/70 group-hover:text-midnight transition-colors">
                {section.label}
              </span>
              <i
                className={`text-sm text-midnight/40 transition-transform duration-300 ${
                  openSection === section.key ? "rotate-45" : ""
                } ri-add-line`}
              />
            </button>
            {openSection === section.key && (
              <div className="pb-4">
                <p className="text-xs text-midnight/60 leading-relaxed">{section.content}</p>
              </div>
            )}
          </div>
        ))}

        {/* Occasions */}
        <div className="border-b border-pearl/40">
          <button
            onClick={() => toggleSection("occasions")}
            className="w-full flex items-center justify-between py-4 text-left cursor-pointer group whitespace-nowrap"
          >
            <span className="text-[11px] tracking-widest uppercase text-midnight/70 group-hover:text-midnight transition-colors">
              Occasions
            </span>
            <i
              className={`text-sm text-midnight/40 transition-transform duration-300 ${
                openSection === "occasions" ? "rotate-45" : ""
              } ri-add-line`}
            />
          </button>
          {openSection === "occasions" && (
            <div className="pb-4">
              <div className="flex flex-wrap gap-2">
                {product.occasions.map((occ) => (
                  <span
                    key={occ}
                    className="text-[10px] tracking-wide border border-pearl/60 text-midnight/50 px-3 py-1"
                  >
                    {occ}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to determine if a color is light (for checkmark contrast)
function isLightColor(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
}
